import type { ReactNode } from 'react';
import type { FoundationFieldName, FoundationStatus } from '@@/api/learnings/foundation/foundation';
import type { FoundationFilter, LearningsSetupManager } from '@/state/learnings/useLearningsSetup';
import { useNavigate } from 'react-router';
import { AiButton } from '@/components/ai-chat';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { NavLink } from '@/components/NavLink';
import { cn } from 'helpers/cn';
import {
  EmptyState,
  FOCUS_RING,
  MICRO_LABEL_SHAPE,
  Pager,
  PillTabs,
  ROW_ACTION_BUTTON,
  TableScroll,
} from '../../ConsolePanel';
import { Section, SetupSteps, StatusPill } from './parts';

const FIELDS: { field: FoundationFieldName; label: string }[] = [
  { field: 'hypothesis', label: 'Hypothesis' },
  { field: 'description', label: 'Description' },
  { field: 'mechanism', label: 'Mechanism' },
  { field: 'pxl', label: 'PXP' },
  { field: 'debrief', label: 'Debrief' },
];

const COLUMNS = '64px minmax(220px,1.8fr) repeat(5,minmax(80px,1fr)) 64px';

const STEPS = [
  { n: '01 reads', title: 'Every test', body: 'Hypothesis, PXP, mechanism, stats, code, screenshots, debrief' },
  { n: '02 connects', title: 'Patterns', body: 'By page, change type and mechanism, with surprises flagged' },
  { n: '03 suggests', title: 'Next tests', body: 'Open questions, one click from a pre-filled experiment' },
];

const tests = (count: number) => `${count} ${count === 1 ? 'test' : 'tests'}`;

type FieldChipProps = { status: FoundationStatus };

const FieldChip = ({ status }: FieldChipProps) => (
  <span
    className={cn(
      MICRO_LABEL_SHAPE,
      'inline-flex rounded-pill border px-2 py-0.5',
      status === 'ok' ? 'border-line text-dim' : 'border-warn-line text-warn',
    )}
  >
    {status}
  </span>
);

type SetupViewProps = { setup: LearningsSetupManager };

/**
 * Before learnings are on: what they do, what each concluded test has written down (homework that
 * makes the learnings sharper, never a gate), and the switch with what it costs.
 */
export const SetupView = ({ setup }: SetupViewProps) => (
  <div className="flex flex-col gap-[14px]">
    <Intro concluded={setup.concluded} />

    <div className="flex flex-wrap items-center justify-between gap-2.5">
      <StatusPill tone="danger">learnings: off</StatusPill>
      <SetupSteps current={1} />
    </div>

    <FoundationCheck setup={setup} />
    <WriteLearnings setup={setup} />

    <ConfirmDialog
      open={setup.confirming}
      title={`${setup.needWork} of ${tests(setup.concluded)} have gaps. Write learnings anyway?`}
      description="Learnings are written from what each test has written down. Tests with a short hypothesis, no expected mechanism or no debrief still get learnings, but vaguer ones. You can fill the gaps later, and the learnings are rewritten when you do."
      confirmLabel="Write learnings"
      onConfirm={setup.confirm}
      onCancel={setup.cancel}
    />
  </div>
);

type IntroProps = { concluded: number };

const Intro = ({ concluded }: IntroProps) => (
  <section className="bg-panel border-line rounded-panel grid overflow-hidden border [grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr))]">
    <div className="flex flex-col gap-2 px-[18px] pb-5 pt-[18px]">
      <span className={cn(MICRO_LABEL_SHAPE, 'text-accent')}>New · experiment learnings</span>
      <h1 className="m-0 max-w-[28ch] text-[20px] font-medium leading-tight tracking-[-0.015em] text-balance">
        Turn {tests(concluded)} into knowledge you can act on
      </h1>
      <p className="text-dim m-0 max-w-[62ch] text-pretty">
        Learnings reads every concluded experiment, connects them and tells you what works on this site, what surprised
        you and what to test next.
      </p>
    </div>
    {STEPS.map((step) => (
      <div key={step.n} className="bg-panel2 border-line flex flex-col gap-1.5 border-l px-3.5 py-[18px]">
        <span className="micro-label">{step.n}</span>
        <span className="font-medium">{step.title}</span>
        <span className="supporting text-pretty">{step.body}</span>
      </div>
    ))}
  </section>
);

export const FoundationCheck = ({ setup }: SetupViewProps) => {
  const counts = setup.counts;
  const options: { label: string; value: FoundationFilter }[] = [
    { label: `Need work · ${counts?.needWork ?? 0}`, value: 'need' },
    { label: `Ready · ${counts?.ready ?? 0}`, value: 'ready' },
    { label: `All · ${counts?.total ?? 0}`, value: 'all' },
  ];
  const first = (setup.currentPage - 1) * 5 + 1;

  return (
    <Section
      title="Foundation check"
      description="Learnings are never better than the tests behind them. Fix gaps first."
      tourAnchor="learnings-foundation"
      actions={
        <PillTabs
          options={options}
          value={setup.filter}
          onChange={(value) => setup.setFilter(value as FoundationFilter)}
        />
      }
    >
      <TableScroll minWidth={860}>
        <div
          role="row"
          className="bg-panel2 border-line grid items-end gap-3 border-b px-3.5 py-2"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          <span className="micro-label">ID</span>
          <span className="micro-label">Experiment</span>
          {FIELDS.map(({ field, label }) => (
            <span key={field} className="micro-label flex flex-col gap-0.5">
              <span>{label}</span>
              <span className="tracking-[0.04em]">
                {counts?.okByField[field] ?? 0}/{counts?.total ?? 0}
              </span>
            </span>
          ))}
          <span className="micro-label text-right">Action</span>
        </div>
        {setup.rows.map((row) => (
          <div
            key={row.experimentId}
            role="row"
            className="border-line grid items-center gap-3 border-b px-3.5 py-[9px]"
            style={{ gridTemplateColumns: COLUMNS }}
          >
            <span className="num text-dim">{row.experimentId}</span>
            <span className="flex min-w-0 flex-col gap-px">
              <span className="text-meta font-medium">{row.name}</span>
              {row.note && <span className="supporting text-warn">{row.note}</span>}
            </span>
            {FIELDS.map(({ field }) => (
              <span key={field} title={row.foundation[field].note ?? undefined}>
                <FieldChip status={row.foundation[field].status} />
              </span>
            ))}
            <span className="flex justify-end">
              <NavLink
                to={`/experiments/${row.experimentId}`}
                title="Fill the gaps in this experiment"
                aria-label={`Fix gaps in ${row.name}`}
                className={cn(ROW_ACTION_BUTTON, FOCUS_RING, 'inline-flex items-center border')}
              >
                Fix
              </NavLink>
            </span>
          </div>
        ))}
      </TableScroll>
      {setup.rows.length === 0 && (
        <div className="p-3.5">
          <EmptyState>
            {setup.filter === 'need'
              ? 'Every concluded test has its foundation in place.'
              : 'No concluded test is ready yet. Fix the gaps above to add them.'}
          </EmptyState>
        </div>
      )}
      {setup.filteredCount > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 pl-3.5">
          <span className="supporting">
            Showing {first} to {Math.min(first + 4, setup.filteredCount)} of {tests(setup.filteredCount)}
          </span>
          {setup.pageCount > 1 && (
            <Pager currentPage={setup.currentPage} totalPages={setup.pageCount} onChange={setup.setPage} />
          )}
        </div>
      )}
    </Section>
  );
};

type SettingRowProps = { label: string; children: ReactNode; last?: boolean };

const SettingRow = ({ label, children, last = false }: SettingRowProps) => (
  <div
    className={cn(
      'grid items-center gap-3 py-3 [grid-template-columns:140px_minmax(0,1fr)]',
      !last && 'border-line border-b',
    )}
  >
    <span className="micro-label">{label}</span>
    <div className="flex flex-wrap items-center gap-3">{children}</div>
  </div>
);

type CheckProps = {
  id: string;
  checked: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  label: string;
};

const Check = ({ id, checked, onChange, disabled = false, label }: CheckProps) => (
  <label
    htmlFor={id}
    className={cn('flex items-center gap-2', disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer')}
  >
    <Checkbox id={id} checked={checked} disabled={disabled} onCheckedChange={(value) => onChange?.(value === true)} />
    <span>{label}</span>
  </label>
);

const WriteLearnings = ({ setup }: SetupViewProps) => {
  const navigate = useNavigate();
  const sharingNote = setup.accountOptedOut
    ? 'Your account has opted out, which covers this website'
    : setup.mayChangeSharing
      ? 'This helps you get better suggestions'
      : 'Only the account that owns this website can change this';

  return (
    <Section
      title="Write learnings"
      description="Reads each test, writes a debrief and pools them into patterns. Uses this account's AI credits."
      tourAnchor="learnings-write"
      footer={
        setup.canManage ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => navigate('/experiments/library')}>
              Not now
            </Button>
            <AiButton onClick={setup.request} busy={setup.isStarting}>
              {setup.past > 0 ? `Write learnings for ${tests(setup.past)}` : 'Turn learnings on'}
            </AiButton>
          </>
        ) : (
          <span className="supporting">Only an admin can turn learnings on.</span>
        )
      }
    >
      <div className="px-3.5">
        <SettingRow label="New tests">
          {/* Learnings write for each new test for as long as they are on; there is no off-and-on-demand. */}
          <Check id="learnings-auto" checked disabled label="Write automatically when a test ends" />
          <span className="supporting">Always on while learnings are on</span>
        </SettingRow>
        <SettingRow label="Screenshots">
          <Check
            id="learnings-screenshots"
            checked={setup.readScreenshots}
            onChange={setup.setReadScreenshots}
            disabled={!setup.canManage}
            label="Read variant screenshots"
          />
          <span className="supporting">More precise understanding of your website</span>
        </SettingRow>
        <SettingRow label="Data sharing">
          <Check
            id="learnings-sharing"
            checked={setup.shareData}
            onChange={setup.setShareData}
            disabled={!setup.canManage || !setup.mayChangeSharing}
            label="Share anonymous data with the analysis engine"
          />
          <span className="supporting">{sharingNote}</span>
        </SettingRow>
        <SettingRow label="AI credits" last>
          <span className="num font-medium">≈ {setup.estimate.toLocaleString()} credits once</span>
          <span className="supporting">
            then ≈ {setup.perTest} per new test · drawn from the account&apos;s AI credits
            {setup.creditsLeft !== null && ` · ${setup.creditsLeft.toLocaleString()} left`}
          </span>
        </SettingRow>
      </div>
    </Section>
  );
};
